import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import httpMethod from 'services/update2025/httpMethod';

import { Toast } from "primereact/toast";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dialog } from "primereact/dialog";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { ProgressSpinner } from "primereact/progressspinner";
import { InputTextarea } from "primereact/inputtextarea";

import moment from "moment";
import { ClientContext } from "components/client/ClientContext";
import { InputTextList } from "components/InputTextList";
import { createClient, findClientSecret, generateClientSecret, updateClient, validateClient } from "services/ClientService";
import { findRoles } from "services/RoleService";
import { handleError } from "utils/handleError";
import { actionUpdate, actionView } from "utils/action";

export const ClientEditor = () => {
    const toast = useRef(null);
    const { t, i18n } = useTranslation(["client", "common"]);
    const tokenObj = httpMethod.getTokenStorage(); // { access_token, … }
    const accessToken = tokenObj?.access_token;;

    let headers = {
        Authorization: `Bearer ${accessToken}`,
        "Accept-Language": i18n.language,
    };

    const clientContext = useContext(ClientContext);
    const { client, resetClient, setClient, clients, setClients, submitted, setSubmitted, clientDialog, setClientDialog } = clientContext;

    const [roles, setRoles] = useState([]);
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [secret, setSecret] = useState();
    const [redirectUris, setRedirectUris] = useState([""]);
    const [webOrigins, setWebOrigins] = useState([""]);
    const [showSpinner, setShowSpinner] = useState(false);
    const [validForm, setValidForm] = useState(false);
    const [titleEditor, setTitleEditor] = useState(t("client.titleCreateDetail"));

    const realmTypes = [
        // { label: t("officer"), value: "OFFICER" },
        { label: t("citizen"), value: "CITIZEN" },
        { label: t("foreign"), value: "FOREIGN" },
    ];

    useEffect(() => {
        findRoles(headers, {
            first: 0,
            rows: 1000,
            page: 0,
            filters: {
                type: { value: "CLIENT", matchMode: "equals" },
            },
        })
            .then((res) => {
                setRoles(res.data);
            })
            .catch((error) => handleError(toast, t, error));
    }, []);

    useEffect(() => {
        // create
        if (!client?.id) {
            setTitleEditor(t("client.titleCreateDetail"));
        } else {
            // update
            if (actionUpdate === client?.action) {
                setTitleEditor(t("client.titleUpdateDetail"));
            }
            // view
            else {
                setTitleEditor(t("client.titleDetail"));
            }
        }
    });

    useEffect(() => {
        setSelectedRoles(client?.roles);
        setRedirectUris(client?.redirectUris?.length > 0 ? client?.redirectUris : [""]);
        setWebOrigins(client?.webOrigins?.length > 0 ? client?.webOrigins : [""]);

        if (client?.id && client?.serviceAccountEnabled) {
            findClientSecret(headers, client?.id)
                .then((res) => {
                    setSecret(res.data);
                })
                .catch((error) => handleError(toast, t, error));
        }
    }, [client]);

    const onInputChange = (e, name) => {
        const val = e.target?.value || e.value || "";
        let _client = { ...client };
        _client[`${name}`] = val;

        setClient(_client);
    };

    const onDateChange = (e, name) => {
        const date = e.target?.value || e.value || new Date();
        const val = moment(date).format("DD/MM/YYYY");
        let _client = { ...client };
        _client[`${name}`] = val;

        setClient(_client);
    };

    const onCheckboxChange = (e, name) => {
        let _client = { ...client };
        _client[`${name}`] = e.checked;
        setClient(_client);
    };

    const onRolesChange = (value) => {
        setSelectedRoles(value);
    };

    const onListInputChange = (name, items) => {
        if (name === "redirectUris") {
            setRedirectUris(items);
        } else if (name === "webOrigins") {
            setWebOrigins(items);
        }
    };

    const onGenerateSecret = () => {
        generateClientSecret(headers, client?.id)
            .then((res) => {
                setSecret(res.data);
            })
            .catch((error) => handleError(toast, t, error));
    };

    const hideDialog = () => {
        setValidForm(false);
        setSubmitted(false);
        setClientDialog(false);
    };

    const saveClientAction = () => {
        setValidForm(true);
        setShowSpinner(true);
        // Creating
        client.redirectUris = redirectUris.filter((e) => e);
        client.webOrigins = webOrigins.filter((e) => e);
        setClient(client);
        validateClient(client)
            .then(() => {
                if (!client?.id) {
                    return createClient(headers, client).then((res) => {
                        toast.current.show({ severity: "success", summary: t("success", { ns: "common" }), detail: t("inform.created", { ns: "common", object: t("client") }), life: 3000 });
                        setValidForm(false);
                        setClientDialog(false);
                        setSubmitted(true);
                        resetClient();
                    });
                } else {
                    // Updating
                    let params = {
                        name: client.name,
                        enabled: client.enabled,
                        description: client.description,
                        startDate: client.startDate,
                        endDate: client.endDate,
                        serviceAccountEnabled: client.serviceAccountEnabled,
                        redirectUris: client.redirectUris,
                        webOrigins: client.webOrigins,
                    };

                    return updateClient(headers, client?.id, params).then((res) => {
                        toast.current.show({ severity: "success", summary: t("success", { ns: "common" }), detail: t("inform.updated", { ns: "common", object: t("client") }), life: 3000 });
                        setValidForm(false);
                        setClientDialog(false);
                        setSubmitted(true);
                        resetClient();

                        /*return updateRoles(headers, client.id, selectedRoles).then((res) => {
                            let _clients = [...clients];
                            const index = findIndexById(client.id);
                            _clients[index] = res.data;

                            toast.current.show({severity: "success", summary: t("success", {ns: "common"}), detail: t("inform.updated", {ns: "common", object: t("client")}), life: 3000});

                            setClients(_clients);
                            setClientDialog(false);
                            resetClient();
                        });*/
                    });
                }
            })
            .catch((error) => handleError(toast, t, error))
            .finally(() => setShowSpinner(false));
    };

    const findIndexById = (id) => {
        let index = -1;
        for (let i = 0; i < clients?.length; i++) {
            if (clients[i].id === id) {
                index = i;
                break;
            }
        }

        return index;
    };

    const dialogFooter = (
        <>
            <Button label={t("cancel", { ns: "common" })} icon="pi pi-times" className="p-button-text" onClick={hideDialog} />
            <Button label={t("save", { ns: "common" })} icon="pi pi-check" onClick={saveClientAction} />
        </>
    );

    const checkView = () => {
        return actionView === client?.action;
    };

    return (
        <>
            <Toast ref={toast} />
            <Dialog visible={clientDialog} style={{ width: "70%" }} header={titleEditor.toUpperCase()} modal className="p-fluid text-center" onHide={hideDialog}>
                <div className="formgrid">
                    <div className="grid text-left">
                        <div className="col-12">
                            <div className="grid">
                                <div className="col-12 md:col-3">
                                    <div className="grid">
                                        {/* client id */}
                                        <div className="field col-12 ">
                                            <label htmlFor="clientId">
                                                {t("clientId")}
                                                <span className="text-danger">*</span>
                                            </label>
                                            <InputText
                                                id="clientId"
                                                value={client?.clientId}
                                                keyfilter={/[^\s]/}
                                                maxLength={100}
                                                readOnly={client?.id}
                                                onChange={(e) => onInputChange(e, "clientId")}
                                                required
                                                autoFocus
                                                className={classNames({ "p-invalid": validForm && !client?.clientId })}
                                            // readOnly={checkView()}
                                            />
                                            {validForm && !client?.clientId && <small className="text-danger">{t("clientId.required")}.</small>}
                                        </div>

                                        {/* secret */}
                                        {client?.serviceAccountEnabled && secret && (
                                            <div className="field col-12">
                                                <label htmlFor="secret">{t("secret")}</label>
                                                <div className="flex flex-row">
                                                    <InputText id="secret" value={secret} readOnly={true} className="mr-2" />
                                                    {client?.clientId !== "citizen-admin" && client?.clientId !== "officer-admin" && client?.clientId !== "foreign-admin" && <Button label={t("generateSecret")} icon="pi pi-replay" className="p-button w-10rem" onClick={onGenerateSecret} />}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* tên tài khoản */}
                                <div className="field col-12 md:col-6">
                                    <label htmlFor="name">
                                        {t("name")}
                                        <span className="text-danger">*</span>
                                    </label>
                                    <InputText id="name" value={client?.name} maxLength={200} onChange={(e) => onInputChange(e, "name")} required className={classNames({ "p-invalid": validForm && !client?.name })} readOnly={checkView()} />
                                    {validForm && !client?.name && <small className="text-danger">{t("name.required")}.</small>}
                                </div>

                                {/* loại đối tượng */}
                                <div className="field col-12 md:col-3">
                                    <label htmlFor="realmType">
                                        {t("realmType")}
                                        <span className="text-danger">*</span>
                                    </label>
                                    <Dropdown
                                        id="realmType"
                                        value={client?.realmType}
                                        options={realmTypes}
                                        disabled={client?.id}
                                        // disabled={checkView()}
                                        onChange={(e) => onInputChange(e, "realmType")}
                                        required
                                        className={classNames({ "p-invalid": validForm && !client?.realmType })}
                                    />
                                    {validForm && !client?.name && <small className="text-danger">{t("client.realmType.required")}.</small>}
                                </div>
                            </div>
                            <div className="grid">
                                {/* ngày bắt đầu */}
                                <div className="field col-12 md:col-3">
                                    <label htmlFor="startDate">
                                        {t("startDate")}
                                        <span className="text-danger">*</span>
                                    </label>
                                    <Calendar
                                        id="startDate"
                                        dateFormat="dd/mm/yy"
                                        required
                                        value={moment(client?.startDate, "DD/MM/YYYY").toDate()}
                                        onChange={(e) => onDateChange(e, "startDate")}
                                        disabled={checkView()}
                                        className={classNames({ "p-invalid": validForm && !client?.startDate })}
                                    ></Calendar>
                                    {validForm && !client?.startDate && <small className="text-danger">{t("client.startDate.required")}.</small>}
                                </div>

                                {/* ngày kết thúc */}
                                <div className="field col-12 md:col-3">
                                    <label htmlFor="endDate">{t("endDate")}</label>
                                    <Calendar id="endDate" dateFormat="dd/mm/yy" value={moment(client?.endDate, "DD/MM/YYYY").toDate()} onChange={(e) => onDateChange(e, "endDate")} disabled={checkView()}></Calendar>
                                </div>

                                {/* sử dụng api */}
                                <div className="field  col-12 md:col-3" style={{ display: "flex", justifyContent: "center", margin: "auto" }}>
                                    <label htmlFor="serviceAccountEnabled">{t("serviceAccountEnabled")}</label>
                                    <Checkbox id="serviceAccountEnabled" checked={client?.serviceAccountEnabled} onChange={(e) => onCheckboxChange(e, "serviceAccountEnabled")} readOnly={checkView()} />
                                </div>

                                {/* kích hoạt */}
                                <div className="field col-12 md:col-3" style={{ display: "flex", justifyContent: "center", margin: "auto" }}>
                                    <label htmlFor="enabled">{t("enabled")}</label>
                                    <Checkbox id="enabled" checked={client?.enabled} onChange={(e) => onCheckboxChange(e, "enabled")} readOnly={checkView()} />
                                </div>
                            </div>
                            <div className="grid">
                                {/* redirect url */}
                                <div className="field col-12 md:col-6">
                                    <label htmlFor="redirectUris">
                                        {t("redirectUris")}
                                        <span className="text-danger">*</span>
                                    </label>
                                    <InputTextList id="redirectUris" value={redirectUris?.length > 0 ? redirectUris : [""]} onChange={(items) => onListInputChange("redirectUris", items)} readOnly={checkView()} className={classNames({ "p-invalid": validForm && client.redirectUris.length < 1 })} />
                                    {validForm && client?.redirectUris.length < 1 && <small className="text-danger">{t("client.redirectUris.required")}.</small>}
                                </div>
                                {/* web origin */}
                                <div className="field col-12 md:col-6">
                                    <label htmlFor="webOrigins">
                                        {t("webOrigins")}
                                        <span className="text-danger">*</span>
                                    </label>
                                    <InputTextList id="webOrigins" value={webOrigins?.length > 0 ? webOrigins : [""]} onChange={(items) => onListInputChange("webOrigins", items)} readOnly={checkView()} className={classNames({ "p-invalid": validForm && client?.webOrigins.length < 1 })} />
                                    {validForm && client?.webOrigins.length < 1 && <small className="text-danger">{t("client.webOrigins.required")}.</small>}
                                </div>
                            </div>
                            <div className="grid">
                                {/* mô tả */}
                                <div className="field col-12">
                                    <label htmlFor="description">{t("description")}</label>
                                    <InputTextarea id="description" value={client?.description || ""} maxLength={500} onChange={(e) => onInputChange(e, "description")} rows={3} cols={20} readOnly={checkView()} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="grid">
                    <div className="col-12">
                        {/* save */}
                        {!checkView() && (
                            <Button
                                style={{
                                    width: "max-content",
                                }}
                                className="mr-2"
                                label={t("save", { ns: "common" })}
                                icon="pi pi-check"
                                onClick={saveClientAction}
                            />
                        )}
                        {/* close */}
                        <Button
                            style={{
                                width: "max-content",
                            }}
                            className="mr-2 p-button-danger"
                            label={t("close", { ns: "common" })}
                            icon="pi pi-times"
                            onClick={hideDialog}
                        />
                    </div>
                </div>
            </Dialog>

            <Dialog visible={showSpinner} closable={false} resizable={false} draggable={false} showHeader={false}>
                <ProgressSpinner />
            </Dialog>
        </>
    );
};
